import { toast } from 'react-toastify';
import { i18n } from '../translate/i18n';
import { isString } from 'lodash';

/**
 * Exibe erro em toast (react-toastify). Visual e opções vêm do ToastContainer.
 * Usado para erros de API/backend: extrai message do response, fallback em i18n ou mensagem genérica.
 *
 * Padrão do projeto:
 * - Em catch de chamadas API: usar toastError(err).
 * - Para sucesso/validação/feedback fixo: usar toast.success(), toast.warning(), toast.info() ou toast.error(msg) direto.
 * - Não usar em componentes de conversa (WhatsApp/Face/Insta) — lá há NotificationToast próprio.
 */
/** Extrai código ERR_* de `message` quando o backend envia só o código ou texto repetido/misto. */
const extractKnownBackendErrorCode = (message) => {
  if (!isString(message)) return null;
  const trimmed = message.trim();
  if (i18n.exists(`backendErrors.${trimmed}`)) return trimmed;
  const match = trimmed.match(/\b(ERR_[A-Z0-9_]+)\b/);
  if (match && i18n.exists(`backendErrors.${match[1]}`)) return match[1];
  return null;
};

const extractAnyBackendErrorCode = (message) => {
  if (!isString(message)) return null;
  const trimmed = message.trim();
  if (/^ERR_[A-Z0-9_]+$/.test(trimmed)) return trimmed;
  const match = trimmed.match(/\b(ERR_[A-Z0-9_]+)\b/);
  return match ? match[1] : null;
};

const translateBackendErrorToken = (token) => {
  const key = `backendErrorTokens.${token}`;
  if (i18n.exists(key)) return i18n.t(key);
  return token.toLowerCase();
};

const humanizeBackendErrorCode = (code) => {
  if (!isString(code)) return null;
  const normalized = code.trim();
  if (!/^ERR_[A-Z0-9_]+$/.test(normalized)) return null;
  const tokens = normalized
    .replace(/^ERR_/, '')
    .split('_')
    .filter(Boolean)
    .map((t) => translateBackendErrorToken(t));

  if (tokens.length === 0) return null;
  const sentence = tokens.join(' ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

const toastError = (err) => {
  if (err?.isHeaderOrCookieTooLarge) {
    return;
  }
  const data = err?.response?.data;
  const backendMessage = isString(data?.message) ? data.message.trim() : null;
  const rawError = data?.error ?? data?.message ?? data?.code;

  let errorCode =
    (isString(rawError) && rawError) ||
    (rawError && isString(rawError.code) && rawError.code) ||
    (rawError && isString(rawError.error) && rawError.error) ||
    (rawError && isString(rawError.message) && rawError.message) ||
    null;

  if (isString(errorCode) && errorCode.includes(':')) {
    errorCode = errorCode.split(':')[0].trim();
  }

  if (backendMessage) {
    const codeFromMessage = extractKnownBackendErrorCode(backendMessage);
    if (codeFromMessage) {
      toast.error(i18n.t(`backendErrors.${codeFromMessage}`), {
        toastId: codeFromMessage,
      });
      return;
    }
    const unknownCodeFromMessage = extractAnyBackendErrorCode(backendMessage);
    if (unknownCodeFromMessage) {
      toast.error(humanizeBackendErrorCode(unknownCodeFromMessage) || backendMessage, {
        toastId: unknownCodeFromMessage,
      });
      return;
    }
    const genericCreatingFollowUp =
      backendMessage === 'Error creating follow-up' ||
      backendMessage === 'Erro ao criar follow-up' ||
      backendMessage === 'Error al crear follow-up';
    const messageToShow = genericCreatingFollowUp
      ? i18n.t('backendErrors.ERR_CREATING_FOLLOW_UP')
      : backendMessage;
    toast.error(messageToShow, {
      toastId: errorCode || messageToShow.slice(0, 50),
    });
    return;
  }

  if (errorCode) {
    if (i18n.exists(`backendErrors.${errorCode}`)) {
      toast.error(i18n.t(`backendErrors.${errorCode}`), {
        toastId: errorCode,
        hideProgressBar: false,
        newestOnTop: false,
        closeOnClick: false,
        rtl: false,
        pauseOnFocusLoss: false,
        draggable: false,
        pauseOnHover: false,
        theme: 'light',
      });
      return;
    }
    const humanized = humanizeBackendErrorCode(errorCode);
    if (humanized) {
      toast.error(humanized, { toastId: errorCode });
      return;
    }
    toast.error(errorCode, { toastId: errorCode });
    return;
  }

  if (isString(err)) {
    toast.error(err);
    return;
  }

  toast.error('An error occurred!');
};

export default toastError;
